import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { EpisodeVisitVitalsReading } from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import {
  CHART_SERIES_KEYS,
  CHART_VITAL_KEYS,
  VITAL_COLUMNS,
  yDomainForValues,
  type ChartVitalKey,
  type VitalKey,
} from './vitalsConfig';

const AXIS_TICK = 'hsl(var(--muted-foreground))';
const GRID_STROKE = 'hsl(var(--border))';
const LINE_PRIMARY = 'hsl(var(--primary))';
const LINE_SECONDARY = 'hsl(var(--chart-2, 215 16% 47%))';

type ChartPoint = {
  visitId: string;
  dateLabel: string;
  visitType: string;
  clinician: string;
} & Partial<Record<ChartVitalKey, number>>;

type EpisodeVitalsChartsProps = {
  readings: EpisodeVisitVitalsReading[];
};

function buildPoints(readings: EpisodeVisitVitalsReading[]): ChartPoint[] {
  return readings.map(reading => {
    const point: ChartPoint = {
      visitId: reading.visit_id,
      dateLabel: formatDateToMMDDYYYY(reading.visit_date) || '—',
      visitType: reading.visit_type_display_name,
      clinician: reading.clinician_name ?? '—',
    };
    for (const key of CHART_SERIES_KEYS) {
      const numeric = reading.vitals[key]?.value;
      if (numeric != null && !Number.isNaN(numeric)) {
        point[key] = numeric;
      }
    }
    return point;
  });
}

function hasSeries(points: ChartPoint[], key: ChartVitalKey): boolean {
  return points.some(p => p[key] != null);
}

/** Keep only visits that have at least one of the series keys (no null gaps). */
function pointsForSeries(
  points: ChartPoint[],
  keys: ChartVitalKey[]
): ChartPoint[] {
  return points.filter(p => keys.some(key => p[key] != null));
}

function valuesForKeys(points: ChartPoint[], keys: ChartVitalKey[]): number[] {
  const values: number[] = [];
  for (const point of points) {
    for (const key of keys) {
      const v = point[key];
      if (v != null && !Number.isNaN(v)) values.push(v);
    }
  }
  return values;
}

function ChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
    payload?: ChartPoint;
  }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className='rounded-md border bg-background px-3 py-2 text-xs shadow-md'>
      <div className='font-medium'>{row.dateLabel}</div>
      <div className='text-muted-foreground'>{row.visitType}</div>
      <div className='text-muted-foreground'>{row.clinician}</div>
      <div className='mt-1 space-y-0.5'>
        {payload.map(item => (
          <div key={String(item.name)} className='flex items-center gap-2'>
            <span
              className='inline-block h-2 w-2 rounded-full'
              style={{ background: item.color }}
            />
            <span>
              {item.name}: {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VitalLineChart({
  title,
  points,
  series,
  domainKey,
}: {
  title: string;
  points: ChartPoint[];
  series: Array<{ key: ChartVitalKey; name: string; color: string }>;
  domainKey: ChartVitalKey;
}) {
  const activeSeries = series.filter(s => hasSeries(points, s.key));
  const chartPoints = pointsForSeries(
    points,
    activeSeries.map(s => s.key)
  );

  const domain = yDomainForValues(
    valuesForKeys(
      chartPoints,
      activeSeries.map(s => s.key)
    ),
    domainKey
  );

  return (
    <div className='rounded-md border p-3'>
      <h4 className='mb-2 text-sm font-medium'>{title}</h4>
      <div className='h-48 w-full min-w-0'>
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart
            data={chartPoints}
            margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
          >
            <CartesianGrid stroke={GRID_STROKE} strokeDasharray='3 3' />
            <XAxis
              dataKey='dateLabel'
              tick={{ fill: AXIS_TICK, fontSize: 11 }}
              tickMargin={6}
            />
            <YAxis
              domain={domain}
              tick={{ fill: AXIS_TICK, fontSize: 11 }}
              width={36}
              allowDecimals
            />
            <Tooltip content={<ChartTooltip />} />
            {activeSeries.length > 1 && (
              <Legend wrapperStyle={{ fontSize: 12 }} />
            )}
            {activeSeries.map(s => (
              <Line
                key={s.key}
                type='monotone'
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function EpisodeVitalsCharts({ readings }: EpisodeVitalsChartsProps) {
  const points = buildPoints(readings);

  const labelFor = (key: VitalKey) =>
    VITAL_COLUMNS.find(c => c.key === key)?.label ?? key;

  const chartDefs: Array<{
    title: string;
    domainKey: ChartVitalKey;
    series: Array<{ key: ChartVitalKey; name: string; color: string }>;
  }> = [
    {
      title: 'Blood pressure',
      domainKey: 'bp_sys',
      series: [
        { key: 'bp_sys', name: 'Sys', color: LINE_PRIMARY },
        { key: 'bp_dia', name: 'Dia', color: LINE_SECONDARY },
      ],
    },
    ...(CHART_VITAL_KEYS.filter(k => k !== 'bp_sys') as ChartVitalKey[]).map(
      key => ({
        title: labelFor(key),
        domainKey: key,
        series: [{ key, name: labelFor(key), color: LINE_PRIMARY }],
      })
    ),
  ];

  const visible = chartDefs.filter(def =>
    def.series.some(s => hasSeries(points, s.key))
  );
  if (visible.length === 0) return null;

  return (
    <section aria-label='Vital trends'>
      <h3 className='mb-2 text-sm font-semibold'>Trends</h3>
      <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
        {visible.map(def => (
          <VitalLineChart
            key={def.title}
            title={def.title}
            points={points}
            series={def.series}
            domainKey={def.domainKey}
          />
        ))}
      </div>
    </section>
  );
}
